import React, { useState } from 'react';
import { Link, useLocation, Navigate, Outlet } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  ShoppingCart,
  Users,
  Tag,
  Star,
  Image,
  FileText,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  AlertTriangle,
  Bell,
  Download,
  Store,
  Megaphone,
  Wrench,
  Bookmark,
  BarChart3,
  UserCheck,
  UserPlus
} from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';

const menuItems = [
  { 
    label: 'Dashboard', 
    icon: LayoutDashboard, 
    path: '/admin/dashboard' 
  },
  { 
    label: 'Productos', 
    icon: Package, 
    path: '/admin/products',
    submenu: [
      { label: 'Todos los productos', path: '/admin/products' },
      { label: 'Inventario', path: '/admin/inventory' },
      { label: 'Agregar producto', path: '/admin/products/new' },
      { label: 'Exportar / Importar', path: '/admin/products/export-import' }
    ]
  },
  { 
    label: 'Marcas', 
    icon: Bookmark, 
    path: '/admin/brands' 
  },
  { 
    label: 'Punto de Venta', 
    icon: Store, 
    path: '/admin/pos',
    submenu: [
      { label: 'Terminal POS', path: '/admin/pos' },
      { label: 'Ubicaciones', path: '/admin/pos/locations' }
    ]
  },
  { 
    label: 'Anuncios', 
    icon: Megaphone, 
    path: '/admin/announcements' 
  },
  { 
    label: 'Pedidos', 
    icon: ShoppingCart, 
    path: '/admin/orders' 
  },
  { 
    label: 'Clientes', 
    icon: Users, 
    path: '/admin/users' 
  },
  { 
    label: 'Cupones', 
    icon: Tag, 
    path: '/admin/coupons' 
  },
  { 
    label: 'Reseñas', 
    icon: Star, 
    path: '/admin/reviews' 
  },
  { 
    label: 'Banners', 
    icon: Image, 
    path: '/admin/banners' 
  },
  { 
    label: 'Promociones', 
    icon: Tag, 
    path: '/admin/promos' 
  },
  { 
    label: 'Influencers', 
    icon: UserCheck, 
    path: '/admin/influencers' 
  },
  { 
    label: 'Leads / Clientes', 
    icon: UserPlus, 
    path: '/admin/customers' 
  },
  { 
    label: 'Analytics', 
    icon: BarChart3, 
    path: '/admin/analytics' 
  },
  { 
    label: 'Contenido', 
    icon: FileText, 
    path: '/admin/content' 
  },
  { 
    label: 'Herramientas', 
    icon: Wrench, 
    path: '/admin/tools' 
  }
];

const AdminLayout = () => {
  const { admin, isAuthenticated, loading, logout } = useAdminAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState([]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const toggleSubmenu = (label) => {
    setExpandedMenus(prev => 
      prev.includes(label) 
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="p-2 bg-gray-800 rounded-lg text-white"
        >
          {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full bg-gray-800 z-40 transition-all duration-300
        ${sidebarOpen ? 'w-64' : 'w-20'}
        ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-700">
          {sidebarOpen ? (
            <Link to="/admin/dashboard" className="flex items-center">
              <img 
                src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vng9vv17_logo-alphatica-blanco.png"
                alt="Alphatica"
                className="h-10 w-auto"
              />
            </Link>
          ) : (
            <Link to="/admin/dashboard" className="flex items-center justify-center w-full">
              <img 
                src="https://customer-assets.emergentagent.com/job_alphatica-store/artifacts/vng9vv17_logo-alphatica-blanco.png"
                alt="A"
                className="h-8 w-auto"
              />
            </Link>
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="hidden lg:block p-2 text-gray-400 hover:text-white transition-colors"
          >
            <Menu size={20} />
          </button>
        </div>

        {/* Menu */}
        <nav className="p-4 space-y-1 overflow-y-auto h-[calc(100%-8rem)]">
          {menuItems.map((item) => (
            <div key={item.label}>
              {item.submenu ? (
                <>
                  <button
                    onClick={() => toggleSubmenu(item.label)}
                    className={`
                      w-full flex items-center justify-between px-3 py-2.5 rounded-lg transition-colors
                      ${isActive(item.path) ? 'bg-amber-500/10 text-amber-500' : 'text-gray-300 hover:bg-gray-700'}
                    `}
                  >
                    <div className="flex items-center gap-3">
                      <item.icon size={20} />
                      {sidebarOpen && <span className="text-sm">{item.label}</span>}
                    </div>
                    {sidebarOpen && (
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform ${expandedMenus.includes(item.label) ? 'rotate-180' : ''}`}
                      />
                    )}
                  </button>
                  {sidebarOpen && expandedMenus.includes(item.label) && (
                    <div className="ml-8 mt-1 space-y-1">
                      {item.submenu.map(sub => (
                        <Link
                          key={sub.path}
                          to={sub.path}
                          onClick={() => setMobileMenuOpen(false)}
                          className={`
                            block px-3 py-2 text-sm rounded-lg transition-colors
                            ${location.pathname === sub.path ? 'text-amber-500' : 'text-gray-400 hover:text-white'}
                          `}
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`
                    flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors
                    ${isActive(item.path) ? 'bg-amber-500/10 text-amber-500' : 'text-gray-300 hover:bg-gray-700'}
                  `}
                >
                  <item.icon size={20} />
                  {sidebarOpen && <span className="text-sm">{item.label}</span>}
                </Link>
              )}
            </div>
          ))}
        </nav>

        {/* User & Logout */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-gray-700">
          {sidebarOpen && (
            <div className="flex items-center gap-3 mb-3 px-3">
              <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-black font-medium">
                {admin?.first_name?.[0] || 'A'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-white truncate">{admin?.first_name} {admin?.last_name}</p>
                <p className="text-xs text-gray-400 truncate">{admin?.email}</p>
              </div>
            </div>
          )}
          <button
            onClick={logout}
            className="w-full flex items-center gap-3 px-3 py-2.5 text-gray-300 hover:bg-gray-700 rounded-lg transition-colors"
          >
            <LogOut size={20} />
            {sidebarOpen && <span className="text-sm">Cerrar Sesión</span>}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`
        min-h-screen transition-all duration-300
        ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}
      `}>
        {/* Top bar */}
        <header className="h-16 bg-gray-800/50 backdrop-blur-sm border-b border-gray-700 flex items-center justify-between px-6">
          <div className="lg:hidden w-10" /> {/* Spacer for mobile menu button */}
          
          <h1 className="text-lg text-white font-medium">
            {menuItems.find(item => isActive(item.path))?.label || 'Admin'}
          </h1>

          <div className="flex items-center gap-4">
            {/* Notifications */}
            <button className="relative p-2 text-gray-400 hover:text-white transition-colors">
              <Bell size={20} />
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* View Store */}
            <a 
              href="/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="hidden sm:block text-sm text-gray-400 hover:text-amber-500 transition-colors"
            >
              Ver tienda →
            </a>
          </div>
        </header>

        {/* Page content */}
        <div className="p-6">
          <Outlet />
        </div>
      </main>

      {/* Mobile overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
